import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import authConfig from '_/space-management/config/auth';
import { membersApi, organizationsApi } from '_/space-management/services/api';
import { MembersModel } from '_/space-management/services/models/members.model';
import { useAuth } from '_/space-management/hooks/AuthContext';
import {
  OrganizationContextData,
  OrganizationsModel,
} from '_/space-management/services/models/organizations.model';

const OrganizationContext = createContext<OrganizationContextData>({} as OrganizationContextData);

type OrganizationType = {
  children?: React.ReactNode;
};

export const OrganizationProvider: React.FC<OrganizationType> = ({ children }) => {
  const [isLoadingOrganization, setIsLoadingOrganization] = useState<boolean>(true);
  const [organizationData, setOrganizationData] = useState<OrganizationsModel>(
    {} as OrganizationsModel
  );
  const { member } = useAuth();

  const getOrganization = useCallback(async (id: string) => {
    try {
      const result = await organizationsApi.getItem(id);
      setOrganizationData(result);
      return result;
    } catch (error) {
      throw error;
    } finally {
      setIsLoadingOrganization(false);
    }
  }, []);

  const getMember = useCallback(
    async (userId: string, organizationId?: string): Promise<MembersModel[]> => {
      try {
        const result = await membersApi.getList({
          userId,
          ...(organizationId && {
            organizationId,
          }),
        });

        const { data } = result;
        return data;
      } catch (error) {
        throw error;
      }
    },
    []
  );

  useEffect(() => {
    async function loadOrganization(): Promise<void> {
      const storedMember = await AsyncStorage.getItem(authConfig.memberKey);
      if (storedMember) {
        const memberObj = JSON.parse(storedMember);
        const organization = await getOrganization(memberObj.organizationId);
        setOrganizationData(organization);
      } else {
        setIsLoadingOrganization(false);
        setOrganizationData({} as OrganizationsModel);
      }
    }
    if (member) {
      loadOrganization();
    }
  }, [getOrganization, member]);

  return (
    <OrganizationContext.Provider
      value={{ getOrganization, getMember, organizationData, isLoadingOrganization }}
    >
      {children}
    </OrganizationContext.Provider>
  );
};

export function useOrganizationContext(): OrganizationContextData {
  const context = useContext(OrganizationContext);

  if (!context) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }

  return context;
}
